import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Eye, EyeOff, BarChart2 } from 'lucide-react'
import { useAllChekiOrders, type ChekiOrderGlobal } from '@/hooks/use-cheki'
import { formatCurrency } from '@/lib/utils'
import { FeatureGate } from '@/components/feature-gate'

// ── Helpers ───────────────────────────────────────────────────────────────────

function getOrderPrice(o: ChekiOrderGlobal): number {
  const f = o.cheki_forms
  if (o.is_group_cheki) {
    return o.is_ots
      ? (f.group_price_ots_idr ?? f.price_ots_idr ?? f.price_idr)
      : (f.group_price_idr ?? f.price_idr)
  }
  return o.is_ots ? (f.price_ots_idr ?? f.price_idr) : f.price_idr
}

function ProgressBar({ pct, color = 'bg-primary' }: { pct: number; color?: string }) {
  return (
    <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
      <div
        className={`h-full rounded-full ${color}`}
        style={{ width: `${Math.max(2, pct)}%` }}
      />
    </div>
  )
}

// ── Config ────────────────────────────────────────────────────────────────────

const FORM_STATUS_CONFIG = {
  draft:  { label: 'Draft',   className: 'bg-gray-100 text-gray-600' },
  open:   { label: 'Buka',    className: 'bg-green-100 text-green-700' },
  closed: { label: 'Ditutup', className: 'bg-red-100 text-red-700' },
} as const

const STATUS_DEFS = [
  { key: 'verified'       as const, label: 'Terverifikasi', color: 'bg-green-500' },
  { key: 'proof_uploaded' as const, label: 'Bukti Dikirim', color: 'bg-blue-500' },
  { key: 'pending'        as const, label: 'Menunggu',      color: 'bg-yellow-500' },
  { key: 'rejected'       as const, label: 'Ditolak',       color: 'bg-red-500' },
  { key: 'cancelled'      as const, label: 'Dibatalkan',    color: 'bg-gray-400' },
]

// ── Page ──────────────────────────────────────────────────────────────────────

export default function ChekiAnalyticsPage() {
  const { data: orders = [], isLoading } = useAllChekiOrders()
  const [showRevenue, setShowRevenue] = useState(false)

  // ── Global totals ──────────────────────────────────────────────────────────
  const totalSubmissions = new Set(
    orders.map((o) =>
      o.is_ots
        ? ((o.custom_data?.transaction_group_id as string | undefined) ?? o.id)
        : (o.session_id ?? o.id)
    )
  ).size

  const totalQty = orders.reduce((s, o) => s + (o.quantity ?? 1), 0)

  const verifiedOrders = orders.filter((o) => o.status === 'verified')
  const verifiedRevenue = verifiedOrders.reduce(
    (s, o) => s + (o.quantity ?? 1) * getOrderPrice(o),
    0
  )

  const resolvedCount = orders.filter(
    (o) => o.status === 'verified' || o.status === 'rejected'
  ).length
  const verificationRate =
    resolvedCount > 0 ? Math.round((verifiedOrders.length / resolvedCount) * 100) : null

  // ── Channel breakdown ──────────────────────────────────────────────────────
  const poOrders = orders.filter((o) => !o.is_ots)
  const otsOrders = orders.filter((o) => o.is_ots)
  const poQty = poOrders.reduce((s, o) => s + (o.quantity ?? 1), 0)
  const otsQty = otsOrders.reduce((s, o) => s + (o.quantity ?? 1), 0)
  const poSubmissions = new Set(poOrders.map((o) => o.session_id ?? o.id)).size
  const otsSubmissions = new Set(
    otsOrders.map(
      (o) => (o.custom_data?.transaction_group_id as string | undefined) ?? o.id
    )
  ).size

  // ── Per-form performance ───────────────────────────────────────────────────
  const formSubmissionSets = new Map<string, Set<string>>()
  const formMap = new Map<string, {
    id: string; title: string; status: string
    submissions: number; qty: number; revenue: number
  }>()
  for (const o of orders) {
    const f = o.cheki_forms
    if (!formMap.has(f.id)) {
      formMap.set(f.id, { id: f.id, title: f.title, status: f.status, submissions: 0, qty: 0, revenue: 0 })
    }
    if (!formSubmissionSets.has(f.id)) formSubmissionSets.set(f.id, new Set())
    const key = o.is_ots
      ? ((o.custom_data?.transaction_group_id as string | undefined) ?? o.id)
      : (o.session_id ?? o.id)
    formSubmissionSets.get(f.id)!.add(key)
    const entry = formMap.get(f.id)!
    entry.qty += o.quantity ?? 1
    if (o.status === 'verified') entry.revenue += (o.quantity ?? 1) * getOrderPrice(o)
  }
  for (const [fid, set] of formSubmissionSets) {
    const entry = formMap.get(fid)
    if (entry) entry.submissions = set.size
  }
  const formPerf = [...formMap.values()].sort((a, b) => b.qty - a.qty)

  // ── Member distribution ────────────────────────────────────────────────────
  const memberMap = new Map<string, { name: string; poQty: number; otsQty: number }>()
  for (const o of orders) {
    if (!o.member_id || !o.idol_members) continue
    const name = o.idol_members.stage_name ?? o.idol_members.name
    const prev = memberMap.get(o.member_id) ?? { name, poQty: 0, otsQty: 0 }
    const q = o.quantity ?? 1
    memberMap.set(o.member_id, {
      name,
      poQty: prev.poQty + (o.is_ots ? 0 : q),
      otsQty: prev.otsQty + (o.is_ots ? q : 0),
    })
  }
  const memberDist = [...memberMap.values()]
    .map((m) => ({ ...m, total: m.poQty + m.otsQty }))
    .sort((a, b) => b.total - a.total)
  const memberTotal = memberDist.reduce((s, m) => s + m.total, 0)

  // ── Daily trend ────────────────────────────────────────────────────────────
  const dateCountMap = new Map<string, number>()
  for (const o of orders) {
    const d = o.created_at.slice(0, 10)
    dateCountMap.set(d, (dateCountMap.get(d) ?? 0) + 1)
  }
  const trend = [...dateCountMap.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([d, count]) => ({
      label: new Date(d + 'T00:00:00').toLocaleDateString('id-ID', {
        day: 'numeric', month: 'short', year: '2-digit',
      }),
      count,
    }))
  const maxTrend = Math.max(...trend.map((t) => t.count), 1)

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <FeatureGate feature="cheki_analytics">
    <div className="space-y-6">

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Analitik Cheki</h1>
        <p className="text-muted-foreground">Ringkasan seluruh form cheki</p>
      </div>

      {/* Loading */}
      {isLoading && <p className="text-muted-foreground">Memuat data...</p>}

      {/* Empty state */}
      {!isLoading && orders.length === 0 && (
        <div className="rounded-xl border border-dashed border-border py-16 text-center text-muted-foreground">
          <BarChart2 className="mx-auto h-8 w-8 mb-3 opacity-40" />
          <p className="text-sm">Belum ada data pembelian untuk dianalisis.</p>
        </div>
      )}

      {orders.length > 0 && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="text-xs text-muted-foreground">Total Pembelian</p>
              <p className="text-2xl font-bold mt-1 tabular-nums">{totalSubmissions}</p>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="text-xs text-muted-foreground">Total Cheki</p>
              <p className="text-2xl font-bold mt-1 tabular-nums">{totalQty}</p>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">Pendapatan Terverifikasi</p>
                <button
                  onClick={() => setShowRevenue((v) => !v)}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showRevenue ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </button>
              </div>
              <p className="text-2xl font-bold mt-1 tabular-nums">
                {showRevenue ? formatCurrency(verifiedRevenue) : '••••••'}
              </p>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="text-xs text-muted-foreground">Tingkat Verifikasi</p>
              <p className="text-2xl font-bold mt-1 tabular-nums">
                {verificationRate != null ? `${verificationRate}%` : '—'}
              </p>
            </div>
          </div>

          {/* Per-form performance */}
          <div className="rounded-xl border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Form</th>
                  <th className="px-4 py-3 text-right font-medium">Pembelian</th>
                  <th className="px-4 py-3 text-right font-medium">Cheki</th>
                  <th className={`px-4 py-3 text-right font-medium ${showRevenue ? '' : 'hidden'}`}>
                    Pendapatan
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {formPerf.map((row) => {
                  const cfg = FORM_STATUS_CONFIG[row.status as keyof typeof FORM_STATUS_CONFIG]
                  return (
                    <tr key={row.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/cheki/${row.id}`}
                            className="font-medium hover:underline text-primary"
                          >
                            {row.title}
                          </Link>
                          {cfg && (
                            <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${cfg.className}`}>
                              {cfg.label}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{row.submissions}</td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold">{row.qty}</td>
                      <td className={`px-4 py-3 text-right tabular-nums ${showRevenue ? '' : 'hidden'}`}>
                        {formatCurrency(row.revenue)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* 2-column: Saluran Pembelian + Distribusi Status */}
          <div className="grid md:grid-cols-2 gap-6">

            {/* Saluran Pembelian */}
            <div className="rounded-xl border border-border bg-card p-5 space-y-4">
              <h3 className="text-sm font-semibold">Saluran Pembelian</h3>
              <div className="space-y-3">
                {([
                  { label: 'PO',  submissions: poSubmissions,  qty: poQty,  color: 'bg-primary' },
                  { label: 'OTS', submissions: otsSubmissions, qty: otsQty, color: 'bg-sky-500' },
                ] as const).map(({ label, submissions, qty, color }) => {
                  const pct = totalQty > 0 ? Math.round((qty / totalQty) * 100) : 0
                  return (
                    <div key={label} className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-medium">{label}</span>
                        <span className="text-muted-foreground">
                          {submissions} pembelian · {qty} cheki · {pct}%
                        </span>
                      </div>
                      <ProgressBar pct={pct} color={color} />
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Distribusi Status */}
            <div className="rounded-xl border border-border bg-card p-5 space-y-4">
              <h3 className="text-sm font-semibold">Distribusi Status</h3>
              <div className="space-y-2.5">
                {STATUS_DEFS.map(({ key, label, color }) => {
                  const count = orders.filter((o) => o.status === key).length
                  if (count === 0) return null
                  const pct = Math.round((count / orders.length) * 100)
                  return (
                    <div key={key} className="space-y-1">
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>{label}</span>
                        <span>{count} ({pct}%)</span>
                      </div>
                      <ProgressBar pct={pct} color={color} />
                    </div>
                  )
                })}
              </div>
            </div>
          </div>

          {/* Distribusi per Member */}
          {memberDist.length > 0 && (
            <div className="rounded-xl border border-border bg-card p-5 space-y-3">
              <h3 className="text-sm font-semibold">Distribusi per Member</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-xs text-muted-foreground">
                    <th className="pb-2 text-left font-medium">Member</th>
                    <th className="pb-2 text-right font-medium">PO</th>
                    <th className="pb-2 text-right font-medium">OTS</th>
                    <th className="pb-2 text-right font-medium">Total</th>
                    <th className="pb-2 text-right font-medium">%</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {memberDist.map((m) => {
                    const pct = memberTotal > 0 ? Math.round((m.total / memberTotal) * 100) : 0
                    return (
                      <tr key={m.name}>
                        <td className="py-2.5 font-medium">{m.name}</td>
                        <td className="py-2.5 text-right text-muted-foreground">{m.poQty > 0 ? m.poQty : '—'}</td>
                        <td className="py-2.5 text-right text-muted-foreground">{m.otsQty > 0 ? m.otsQty : '—'}</td>
                        <td className="py-2.5 text-right font-semibold">{m.total}</td>
                        <td className="py-2.5 text-right text-muted-foreground">{pct}%</td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t border-border font-semibold">
                    <td className="pt-2.5">Total</td>
                    <td className="pt-2.5 text-right">
                      {memberDist.reduce((s, m) => s + m.poQty, 0) > 0
                        ? memberDist.reduce((s, m) => s + m.poQty, 0)
                        : '—'}
                    </td>
                    <td className="pt-2.5 text-right">
                      {memberDist.reduce((s, m) => s + m.otsQty, 0) > 0
                        ? memberDist.reduce((s, m) => s + m.otsQty, 0)
                        : '—'}
                    </td>
                    <td className="pt-2.5 text-right">{memberTotal}</td>
                    <td className="pt-2.5 text-right text-muted-foreground font-normal">100%</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Aktivitas per Hari */}
          {trend.length > 0 && (
            <div className="rounded-xl border border-border bg-card p-5 space-y-3">
              <h3 className="text-sm font-semibold">Aktivitas per Hari</h3>
              <div className="space-y-1.5">
                {trend.map(({ label, count }) => (
                  <div key={label} className="flex items-center gap-3 text-xs">
                    <span className="w-24 shrink-0 text-muted-foreground text-right">{label}</span>
                    <div className="flex-1 bg-muted rounded-full h-4 overflow-hidden">
                      <div
                        className="h-full bg-primary/70 rounded-full flex items-center justify-end pr-2"
                        style={{ width: `${Math.max(2, Math.round((count / maxTrend) * 100))}%` }}
                      >
                        {count >= 3 && (
                          <span className="text-[10px] font-medium text-primary-foreground">{count}</span>
                        )}
                      </div>
                    </div>
                    {count < 3 && <span className="shrink-0 font-medium">{count}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
    </FeatureGate>
  )
}
